import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  UserPlus, Trash2, KeyRound, Shield, Users as UsersIcon,
  Search, X, Copy, Check,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = (t) => ({ headers: { "X-Admin-Token": t } });

const ROLE_LABELS = {
  superadmin: "Super Admin",
  founders_office: "Founder's Office Admin",
};
const ROLE_TONE = {
  superadmin: "bg-[#ff5722]/10 text-[#ff5722] border-[#ff5722]/30",
  founders_office: "bg-blue-100 text-blue-800 border-blue-300",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function safeErrorMsg(err, fallback = "Something went wrong") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  return d?.msg || err?.message || fallback;
}

/**
 * AdminUsers — Super-admin-only management screen.
 * Founders-Office users never see this component (Admin.jsx hides the nav item).
 */
export default function AdminUsers({ token, currentUser }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // {user}

  const isSuperadmin = currentUser?.role === "superadmin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users/`, auth(token));
      setRows(data || []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Super admin only");
      else toast.error(safeErrorMsg(e, "Failed to load users"));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token && isSuperadmin) load(); /* eslint-disable-next-line */ }, [token]);

  const filtered = useMemo(() =>
    rows.filter((r) => !q || (`${r.username} ${r.name} ${ROLE_LABELS[r.role] || r.role}`).toLowerCase().includes(q.toLowerCase())),
    [rows, q],
  );

  const remove = async (u) => {
    if (u.id === currentUser?.id) { toast.error("You cannot delete your own account."); return; }
    if (!window.confirm(`Remove ${u.name} (${u.username})? This can’t be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, auth(token));
      toast.success(`Removed ${u.name}`);
      setRows((xs) => xs.filter((x) => x.id !== u.id));
    } catch (e) { toast.error(safeErrorMsg(e, "Delete failed")); }
  };

  if (!isSuperadmin) {
    return (
      <div className="bg-white border border-black/10 p-10 text-center rounded-sm">
        <Shield className="mx-auto h-8 w-8 text-[#ff5722]" />
        <h3 className="mt-3 font-bold text-lg text-[#0a0a0a]">Super admin only</h3>
        <p className="mt-1 text-sm text-[#4a4a4a]">
          The users module is restricted to super admins. Ask a super admin to grant you access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><UsersIcon className="h-5 w-5 text-[#ff5722]" /> Admin users</h2>
          <p className="text-xs text-[#4a4a4a] mt-0.5">Manage who can access the Reachvel dashboard and reset their passwords.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9a9a9a]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…"
              data-testid="admin-users-search"
              className="font-mono text-xs border border-black/15 rounded-full pl-8 pr-3 py-1.5 w-56 outline-none focus:border-[#ff5722] focus:ring-2 focus:ring-[#ff5722]/20" />
          </div>
          <button onClick={() => setShowCreate(true)} data-testid="admin-users-new"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#ff5722] text-white rounded-full hover:bg-[#0a0a0a] transition-colors">
            <UserPlus className="h-3.5 w-3.5" /> New user
          </button>
        </div>
      </div>

      <div className="bg-white border border-black/10 rounded-sm">
        {loading ? (
          <div className="p-8 text-sm text-[#4a4a4a] animate-pulse">Loading users…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-sm text-[#4a4a4a] text-center">No admin users match your search.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[800px]">
              <thead className="bg-[#f7f6f3] border-b border-black/10 text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">
                <tr>
                  <th className="px-4 py-3 w-12">#</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Username</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u, i) => (
                  <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-b border-black/5 hover:bg-[#0a0a0a]/[0.02]">
                    <td className="px-4 py-4 text-xs font-mono text-[#4a4a4a]">{String(i + 1).padStart(2, "0")}</td>
                    <td className="px-4 py-4">
                      <div className="font-semibold text-[#0a0a0a]">{u.name}</div>
                      {u.id === currentUser?.id && <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-emerald-700">You</div>}
                    </td>
                    <td className="px-4 py-4 font-mono text-sm">{u.username}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-block whitespace-nowrap px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.1em] border rounded-full ${ROLE_TONE[u.role] || "bg-zinc-100 border-zinc-300"}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-xs font-mono text-[#4a4a4a]">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => setResetTarget(u)}
                          data-testid={`admin-user-reset-${u.id}`}
                          title="Reset password"
                          className="p-1.5 rounded hover:bg-[#ff5722]/10 text-[#ff5722]">
                          <KeyRound className="h-4 w-4" />
                        </button>
                        <button onClick={() => remove(u)}
                          data-testid={`admin-user-delete-${u.id}`}
                          disabled={u.id === currentUser?.id}
                          title={u.id === currentUser?.id ? "You can’t delete yourself" : "Remove user"}
                          className="p-1.5 rounded hover:bg-rose-100 text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateUserModal token={token} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}
      {resetTarget && (
        <ResetPasswordModal token={token} user={resetTarget} onClose={() => setResetTarget(null)} />
      )}
    </div>
  );
}

function CreateUserModal({ token, onClose, onCreated }) {
  const [form, setForm] = useState({ username: "", name: "", password: "", role: "founders_office" });
  const [busy, setBusy] = useState(false);

  const gen = () => {
    // Simple secure-ish generated password shown once to the super admin.
    const pool = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    let p = "";
    for (let i = 0; i < 12; i++) p += pool[Math.floor(Math.random() * pool.length)];
    setForm((f) => ({ ...f, password: p }));
  };

  const save = async () => {
    if (!form.username.trim() || !form.name.trim() || !form.password) {
      toast.error("Username, name and password are required");
      return;
    }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/users/`, {
        username: form.username.trim().toLowerCase(),
        name: form.name.trim(),
        password: form.password,
        role: form.role,
      }, auth(token));
      toast.success(`Created ${form.name}. Share the password securely — it can’t be retrieved later.`);
      onCreated();
    } catch (e) { toast.error(safeErrorMsg(e, "Failed to create user")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md rounded-lg shadow-2xl">
        <div className="px-5 py-4 border-b border-black/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold">New admin user</div>
            <div className="font-bold text-[#0a0a0a] mt-0.5">Create account</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#4a4a4a] font-bold">Full name</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="new-user-name"
              placeholder="Priya Sharma"
              className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm outline-none focus:border-[#ff5722]" />
          </label>
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#4a4a4a] font-bold">Username</span>
            <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
              data-testid="new-user-username"
              placeholder="priya"
              className="mt-1 w-full font-mono border border-black/15 rounded-md px-3 py-2 text-sm outline-none focus:border-[#ff5722]" />
            <span className="text-[10px] text-[#9a9a9a]">Lowercase letters, digits, dot / dash / underscore. 3-40 characters.</span>
          </label>
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#4a4a4a] font-bold">Role</span>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="new-user-role"
              className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm outline-none focus:border-[#ff5722]">
              <option value="founders_office">Founder's Office Admin</option>
              <option value="superadmin">Super Admin</option>
            </select>
          </label>
          <label className="block">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#4a4a4a] font-bold">Password</span>
              <button type="button" onClick={gen} className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#ff5722] hover:underline">Generate</button>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="new-user-password"
                placeholder="At least 8 chars"
                className="flex-1 font-mono border border-black/15 rounded-md px-3 py-2 text-sm outline-none focus:border-[#ff5722]" />
              {form.password && <CopyBtn value={form.password} />}
            </div>
            <span className="text-[10px] text-amber-700 mt-1 block">⚠ Copy and share this now. You won’t be able to view it later.</span>
          </label>
        </div>
        <div className="px-5 py-4 border-t border-black/10 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs border border-black/15 rounded-full">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="new-user-save"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs bg-[#ff5722] text-white rounded-full hover:bg-[#0a0a0a] disabled:opacity-60">
            <UserPlus className="h-3.5 w-3.5" /> {busy ? "Creating…" : "Create user"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ token, user, onClose }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const gen = () => {
    const pool = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    let p = "";
    for (let i = 0; i < 12; i++) p += pool[Math.floor(Math.random() * pool.length)];
    setPwd(p);
  };

  const save = async () => {
    if (pwd.length < 8) { toast.error("Password must be at least 8 chars"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/users/${user.id}/reset-password`, { new_password: pwd }, auth(token));
      toast.success(`Reset password for ${user.name}. Share the new password securely.`);
      onClose();
    } catch (e) { toast.error(safeErrorMsg(e, "Password reset failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md rounded-lg shadow-2xl">
        <div className="px-5 py-4 border-b border-black/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold">Reset password</div>
            <div className="font-bold text-[#0a0a0a] mt-0.5">{user.name} · @{user.username}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#4a4a4a] font-bold">New password</span>
            <button type="button" onClick={gen} className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#ff5722] hover:underline">Generate</button>
          </div>
          <div className="flex items-center gap-2">
            <input type="text" value={pwd} onChange={(e) => setPwd(e.target.value)}
              data-testid="reset-user-password"
              placeholder="At least 8 chars"
              className="flex-1 font-mono border border-black/15 rounded-md px-3 py-2 text-sm outline-none focus:border-[#ff5722]" />
            {pwd && <CopyBtn value={pwd} />}
          </div>
          <span className="text-[10px] text-amber-700 block">⚠ All existing sessions for this user will be signed out.</span>
        </div>
        <div className="px-5 py-4 border-t border-black/10 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs border border-black/15 rounded-full">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="reset-user-save"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs bg-[#ff5722] text-white rounded-full hover:bg-[#0a0a0a] disabled:opacity-60">
            <KeyRound className="h-3.5 w-3.5" /> {busy ? "Resetting…" : "Reset password"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try { await navigator.clipboard.writeText(value); }
    catch { const t = document.createElement("textarea"); t.value = value; document.body.appendChild(t); t.select(); document.execCommand("copy"); t.remove(); }
    setCopied(true); setTimeout(() => setCopied(false), 1200);
  };
  return (
    <button type="button" onClick={doCopy} title="Copy"
      className="p-2 border border-black/15 rounded-md text-[#0a0a0a] hover:border-[#ff5722] hover:text-[#ff5722]">
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}
